import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Pricing.css';

function Pricing() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [paymentType, setPaymentType] = useState('oneTime'); // 'oneTime' or 'monthly'

  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };

  const pricingPlans = [
    {
      name: "Starter Pack",
      credits: "150",
      oneTimePrice: "$50",
      monthlyPrice: "$40",
      bestFor: "Small businesses starting out with address management",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
    {
      name: "Growth Pack",
      credits: "600",
      oneTimePrice: "$150",
      monthlyPrice: "$120",
      bestFor: "Growing businesses with regular contact management",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    },
    {
      name: "Scale Pack",
      credits: "2,000",
      oneTimePrice: "$300",
      monthlyPrice: "$240",
      bestFor: "Established businesses with high volume needs",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ],
      popular: true
    },
    {
      name: "Enterprise",
      credits: "5,000",
      oneTimePrice: "$500",
      monthlyPrice: "$400",
      bestFor: "Large organizations with specific requirements",
      features: [
        "No expiration date",
        "Use for any HubSpot portal",
        "HubSpot integration"
      ]
    }
  ];

  const faqs = [
    {
      question: "What is a credit?",
      answer: "A credit represents one ZIP code lookup. Each time you enter a ZIP code that triggers our service to populate address fields, one credit is consumed."
    },
    {
      question: "Do credits expire?",
      answer: "No, your credits never expire. Use them at your own pace without worrying about timelines."
    },
    {
      question: "Can I purchase a custom amount of credits?",
      answer: "Yes, if you need a specific number of credits not covered by our standard packages, please contact us for a custom quote."
    },
    {
      question: "What postal codes are supported?",
      answer: "Our service primarily supports 5-digit US ZIP codes. We offer limited support for Canadian postal codes."
    },
    {
      question: "Can I backfill existing contacts?",
      answer: "Yes, if you have access to HubSpot workflows, you can use our service to backfill address information for existing contacts that already have ZIP codes."
    },
    {
      question: "How do monthly subscriptions work?",
      answer: "Monthly subscriptions automatically add credits to your account each month. You can cancel anytime through your account settings. Credits from subscriptions never expire, just like one-time purchases."
    }
  ];

  const handlePaymentTypeChange = (type) => {
    console.log('Pricing page - Changing payment type to:', type); // Debug log
    setPaymentType(type);
  };

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        <div className="pricing-shapes">
          <div className="pricing-shape shape1"></div>
          <div className="pricing-shape shape2"></div>
          <div className="pricing-shape shape3"></div>
        </div>
        <h1>Simple, Transparent Pricing</h1>
        <p className="pricing-intro">
          Pay only for what you use with our credit-based system. Choose between one-time purchases or save 20% when you choose a monthly subscription.
        </p>
        
        {/* Payment Type Toggle */}
        <div className="payment-type-toggle">
          <button 
            className={`toggle-btn ${paymentType === 'oneTime' ? 'active' : ''}`}
            onClick={() => handlePaymentTypeChange('oneTime')}
          >
            One-Time Purchase
          </button>
          <button 
            className={`toggle-btn ${paymentType === 'monthly' ? 'active' : ''}`}
            onClick={() => handlePaymentTypeChange('monthly')}
          >
            Monthly Subscription
          </button>
        </div>
      </div>

      <div className="pricing-plans">
        {pricingPlans.map((plan, index) => {
          const currentPrice = paymentType === 'monthly' ? plan.monthlyPrice : plan.oneTimePrice;
          const period = paymentType === 'monthly' ? 'per month' : 'one-time payment';
          const creditsLabel = paymentType === 'monthly' ? 'Credits per month' : 'Credits';
          
          return (
            <div key={index} className={`pricing-plan ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <div className="popular-badge">Most Popular</div>}
              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price">
                <span className="price">{currentPrice}</span>
                <span className="period">{period}</span>
              </div>
              <div className="plan-credits">
                <span className="credits-amount">{plan.credits}</span>
                <span className="credits-label">{creditsLabel}</span>
              </div>
              <p className="plan-description">{plan.bestFor}</p>
              <ul className="plan-features">
                {plan.features.map((feature, i) => (
                  <li key={i}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="plan-action">
                <Link to="/register" className="plan-button">
                  Get Started
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <div className="enterprise-cta">
        <h2>Need a Custom Solution?</h2>
        <p>For larger volumes or specific requirements, contact our sales team for a tailored package.</p>
        <a href="mailto:info@zipcodeautofill.com" className="enterprise-button">
          Contact Us
        </a>
      </div>

      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${activeFaq === index ? 'active' : ''}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="faq-question">
                <h3>{faq.question}</h3>
                <div className="faq-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12" className="vertical-line"></line>
                  </svg>
                </div>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-guarantee">
        <div className="guarantee-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          </svg>
        </div>
        <div className="guarantee-content">
          <h3>Our Guarantee</h3>
          <p>
            Credits are only consumed for successful lookups. If a ZIP code cannot be found or is invalid, 
            no credit is deducted from your account. If you ever have a credit dispute, please reach out to our Support team and we will take care of you.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
